import React, { useRef, useState, useEffect, useCallback, useMemo, memo, forwardRef } from "react";
import Webcam from "react-webcam";
import PropTypes from "prop-types";
import { CAMERA_HEIGHT, DEFAULT_VIDEO_CONSTRAINTS } from "./constants";
import { playShutterSound } from "./utils";
import { captureFromWebcam } from "./services/cameraCapture";
import {
  useShutterFlash,
  useCameraTrack,
  useFocusIndicator,
  usePinchZoom,
  useImageCaptureFocus,
  useComposedRef,
} from "./hooks/useCameraPreview";

// -----------------------------------------------------------------------------
// Styles (static where possible to avoid re-renders)
// -----------------------------------------------------------------------------

const WEBCAM_STYLE = {
  height: "100%",
  maxHeight: "100vh",
  width: "100%",
  objectFit: "cover",
  backgroundColor: "black",
};

const WEBCAM_STYLE_FILL = { ...WEBCAM_STYLE, height: "100%" };

// -----------------------------------------------------------------------------
// Error boundary (catches render errors in camera tree)
// -----------------------------------------------------------------------------

class CameraErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (process.env.NODE_ENV === "development") {
      console.error("CameraErrorBoundary:", error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError && this.props.fallback) {
      return this.props.fallback(this.state.error);
    }
    return this.props.children;
  }
}

CameraErrorBoundary.propTypes = {
  children: PropTypes.node,
  fallback: PropTypes.func,
};

// -----------------------------------------------------------------------------
// Subcomponents
// -----------------------------------------------------------------------------

const GridOverlay = memo(function GridOverlay() {
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }} aria-hidden>
      <span className="absolute left-1/3 top-0 w-px h-full bg-white/30" />
      <span className="absolute left-2/3 top-0 w-px h-full bg-white/30" />
      <span className="absolute left-0 top-1/3 w-full h-px bg-white/30" />
      <span className="absolute left-0 top-2/3 w-full h-px bg-white/30" />
    </div>
  );
});

const FocusIndicator = memo(function FocusIndicator({ position, active }) {
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 2 }} aria-hidden>
      <div
        className="absolute w-16 h-16 -translate-x-1/2 -translate-y-1/2 transition-all duration-200"
        style={{ left: `${position.x}%`, top: `${position.y}%` }}
      >
        <div
          className={`w-full h-full relative transition-transform duration-150 ${
            active ? "scale-125" : "scale-100"
          }`}
        >
          <span className="absolute left-1/2 top-0 w-px h-full -translate-x-px bg-white/80" />
          <span className="absolute left-0 top-1/2 w-full h-px -translate-y-px bg-white/80" />
          <span className="absolute left-0 top-0 w-3 h-3 border-l-2 border-t-2 border-white/90" />
          <span className="absolute right-0 top-0 w-3 h-3 border-r-2 border-t-2 border-white/90" />
          <span className="absolute left-0 bottom-0 w-3 h-3 border-l-2 border-b-2 border-white/90" />
          <span className="absolute right-0 bottom-0 w-3 h-3 border-r-2 border-b-2 border-white/90" />
        </div>
      </div>
    </div>
  );
});

FocusIndicator.propTypes = {
  position: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }).isRequired,
  active: PropTypes.bool,
};

const MediaErrorFallback = memo(function MediaErrorFallback({ error, onRetry }) {
  const message = error?.message || "Camera unavailable";
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/90 text-white p-4"
      style={{ zIndex: 10 }}
      role="alert"
    >
      <p className="text-sm text-center max-w-sm">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="px-4 py-2 rounded-full bg-white/20 hover:bg-white/30 text-sm font-medium"
        >
          Try again
        </button>
      )}
    </div>
  );
});

MediaErrorFallback.propTypes = {
  error: PropTypes.instanceOf(Error),
  onRetry: PropTypes.func,
};

// -----------------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------------

const CameraPreview = forwardRef(function CameraPreview(
  {
    isOn = false,
    onCapture,
    shouldCapture = false,
    deviceId,
    facingMode = "user",
    playShutterSound: playShutterSoundProp = true,
    showGrid = true,
    showFocusIndicator = true,
    flashOn = false,
    onTorchSupportChange,
    zoomLevel = 1,
    onZoomSupportChange,
    onZoomDelta,
    onDeviceChange,
    onStreamReady,
    onRetry,
    fillHeight = false,
  },
  ref
) {
  const camRef = useRef(null);
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const streamRef = useRef(null);

  const [mediaError, setMediaError] = useState(null);

  const { shutterVisible, triggerShutterFlash } = useShutterFlash();
  const { onUserMedia: onUserMediaFromHook } = useCameraTrack(trackRef, streamRef, {
    flashOn,
    zoomLevel,
    onTorchSupportChange,
    onZoomSupportChange,
    onDeviceChange,
  });
  const handleUserMedia = useCallback(
    (stream) => {
      setMediaError(null);
      onUserMediaFromHook(stream);
      onStreamReady?.();
    },
    [onUserMediaFromHook, onStreamReady]
  );
  const handleUserMediaError = useCallback((err) => {
    setMediaError(err instanceof Error ? err : new Error(String(err)));
  }, []);

  const { focusPosition, focusActive, handleFocusTap } = useFocusIndicator(containerRef);
  const { handleTouchStart, handleTouchMove, handleTouchEnd } = usePinchZoom(onZoomDelta);
  useImageCaptureFocus(camRef, focusPosition);
  const setRef = useComposedRef(ref, camRef);

  // Do not mix facingMode with deviceId. Facing-only: omit width/height — fixed 1280×720 often yields a black front preview on phones.
  const videoConstraints = useMemo(() => {
    if (deviceId) {
      return {
        width: { ideal: DEFAULT_VIDEO_CONSTRAINTS.width },
        height: { ideal: DEFAULT_VIDEO_CONSTRAINTS.height },
        deviceId: { exact: deviceId },
      };
    }
    return { facingMode };
  }, [deviceId, facingMode]);

  const containerStyle = useMemo(
    () => ({
      position: "relative",
      height: fillHeight ? "100%" : CAMERA_HEIGHT,
      width: "100%",
      overflow: "hidden",
    }),
    [fillHeight]
  );

  const captureHandledRef = useRef(false);

  const capture = useCallback(() => {
    const doCapture = () => {
      const node = camRef.current;
      const containerRect =
        fillHeight && containerRef.current ? containerRef.current.getBoundingClientRect() : null;
      const imageSrc = captureFromWebcam(node, {
        fillHeight,
        containerRect: containerRect
          ? { width: containerRect.width, height: containerRect.height }
          : null,
      });
      if (imageSrc) onCapture?.(imageSrc);
      if (playShutterSoundProp) playShutterSound();
      triggerShutterFlash();
    };

    if (fillHeight) {
      requestAnimationFrame(() => requestAnimationFrame(doCapture));
    } else {
      requestAnimationFrame(doCapture);
    }
  }, [onCapture, playShutterSoundProp, fillHeight, triggerShutterFlash]);

  useEffect(() => {
    if (!shouldCapture) {
      captureHandledRef.current = false;
      return;
    }
    if (captureHandledRef.current) return;
    captureHandledRef.current = true;
    capture();
  }, [shouldCapture, capture]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" || e.key === " ") e.currentTarget.click();
  }, []);

  const handleRetry = useCallback(() => {
    setMediaError(null);
    onRetry?.();
  }, [onRetry]);

  if (!isOn) return null;

  const content = (
    <div
      ref={containerRef}
      style={containerStyle}
      className="relative cursor-crosshair"
      onClick={handleFocusTap}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label="Tap to focus"
    >
      <Webcam
        key={deviceId ? `id:${deviceId}` : `face:${facingMode}`}
        audio={false}
        ref={setRef}
        screenshotFormat="image/png"
        screenshotQuality={1}
        height={fillHeight ? undefined : CAMERA_HEIGHT}
        style={fillHeight ? WEBCAM_STYLE_FILL : WEBCAM_STYLE}
        videoConstraints={videoConstraints}
        mirrored={false}
        onUserMedia={handleUserMedia}
        onUserMediaError={handleUserMediaError}
      />
      {mediaError && <MediaErrorFallback error={mediaError} onRetry={handleRetry} />}
      {shutterVisible && <div className="shutter" />}
      {showGrid && <GridOverlay />}
      {showFocusIndicator && <FocusIndicator position={focusPosition} active={focusActive} />}
    </div>
  );

  return (
    <CameraErrorBoundary
      fallback={(error) => (
        <div
          className="flex flex-col items-center justify-center gap-2 bg-black/95 text-white p-4 rounded-lg min-h-[120px]"
          style={containerStyle}
          role="alert"
        >
          <p className="text-sm text-center">Something went wrong</p>
          <p className="text-xs text-white/70">{error?.message}</p>
        </div>
      )}
    >
      {content}
    </CameraErrorBoundary>
  );
});

CameraPreview.propTypes = {
  isOn: PropTypes.bool,
  onCapture: PropTypes.func,
  shouldCapture: PropTypes.bool,
  deviceId: PropTypes.string,
  facingMode: PropTypes.oneOf(["user", "environment"]),
  playShutterSound: PropTypes.bool,
  showGrid: PropTypes.bool,
  showFocusIndicator: PropTypes.bool,
  flashOn: PropTypes.bool,
  onTorchSupportChange: PropTypes.func,
  zoomLevel: PropTypes.number,
  onZoomSupportChange: PropTypes.func,
  onZoomDelta: PropTypes.func,
  onDeviceChange: PropTypes.func,
  onStreamReady: PropTypes.func,
  onRetry: PropTypes.func,
  fillHeight: PropTypes.bool,
};

CameraPreview.defaultProps = {
  isOn: false,
  onCapture: undefined,
  shouldCapture: false,
  deviceId: undefined,
  facingMode: "user",
  playShutterSound: true,
  showGrid: true,
  showFocusIndicator: true,
  flashOn: false,
  onTorchSupportChange: undefined,
  zoomLevel: 1,
  onZoomSupportChange: undefined,
  onZoomDelta: undefined,
  onDeviceChange: undefined,
  onStreamReady: undefined,
  onRetry: undefined,
  fillHeight: false,
};

export default memo(CameraPreview);
